import React from "react";
import * as arrow from "apache-arrow";
import { Box, IconButton, TextField, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestoreIcon from "@mui/icons-material/Restore";
import CompactTableTextField from "../components/transformed_custom_view/viewer/CompactTableTextField";
import { makeGetRowFromArrowTableWithIndices, getSortedRowIndices } from "./mui_table_utils";
import { MUIFlatTableColumn } from "../components/universal_mui_table/MUIFlatTable";
import { DatePivotParams } from "../entities/date_pivot_params";
import {
  DeliveryDateEdits,
  EditEntry,
} from "../contexts/purchasing_calendar_edits/type";

// Columns that should never be shown in the table
const HIDDEN_COLUMNS = new Set([
  "default_lead_time_bucket_days",
  "default_lead_time_bucket_days_min",
  "default_lead_time_bucket_days_max",
  "delivery_date_min",
  "delivery_date_max",
  "calculated_lead_time_min",
  "calculated_lead_time_max",
  "days_change_vs_default_min",
  "days_change_vs_default_max",
  "is_edited_any",
  "is_deleted",
  "date",
]);

// Columns handled explicitly (not shown as generic read-only)
const EXPLICIT_COLUMNS = new Set([
  "planning_cycle",
  "order_date",
  ...HIDDEN_COLUMNS,
]);

// Known segmentation columns
const KNOWN_SEGMENT_COLUMNS = ["supplier", "location", "sku"];

// Hardcoded display labels for known columns
const COLUMN_LABELS: Record<string, string> = {
  planning_cycle: "Planning Cycle",
  order_date: "Order Date",
  supplier: "Supplier",
  location: "Location",
  sku: "SKU",
  default_lead_time_bucket_days_min: "Default Lead Time",
  delivery_date: "Delivery Date",
  calculated_lead_time: "Lead Time (days)",
  days_change_vs_default: "Days Change",
};

const MS_PER_DAY = 86400000;

export function getPurchasingCalendarTableData(args: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  viewParams: DatePivotParams;
  purchasingCalendarConfig?: {
    deliveryDateEdits: DeliveryDateEdits;
    setDeliveryDateEdits: React.Dispatch<
      React.SetStateAction<DeliveryDateEdits>
    >;
    editMode?: boolean;
  };
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  onSort: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => void;
  columnSchema: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  theme: any;
  expandedGroups: Set<string>;
  setExpandedGroups: React.Dispatch<React.SetStateAction<Set<string>>>;
  columnHeaderMap?: Record<string, string>;
  firstMount: React.MutableRefObject<boolean>;
}) {
  const {
    arrowTable,
    columns,
    purchasingCalendarConfig,
    sortConfig,
    onSort,
  } = args;

  const deliveryDateEdits = purchasingCalendarConfig?.deliveryDateEdits ?? {};
  const setDeliveryDateEdits = purchasingCalendarConfig?.setDeliveryDateEdits ?? (() => {});
  const editMode = purchasingCalendarConfig?.editMode ?? false;

  const numRows = arrowTable.numRows;
  let visibleRows = Array.from({ length: numRows }, (_, i) => i);
  if (sortConfig && sortConfig.length > 0) {
    visibleRows = getSortedRowIndices(arrowTable, columns, sortConfig);
  }

  const getRow = makeGetRowFromArrowTableWithIndices(
    arrowTable,
    columns,
    visibleRows
  );

  const segments = args.viewParams.segments ?? [];
  const columnNames = columns.map((c) => c.name);

  // --- Build columns in order ---
  const allColumns: MUIFlatTableColumn[] = [];

  // 0. Delete/restore button column (only in edit mode)
  if (editMode) {
    allColumns.push({
      key: "__delete_action",
      label: "",
      renderHeader: () => React.createElement(React.Fragment, null),
      align: "center" as const,
      sortable: false,
      sticky: true,
      width: 40,
      renderCell: (row: any) => {
        const editKey = buildEditKey(row, segments);
        const edit = deliveryDateEdits[editKey];
        const rowIsDeletedFromVT = !!row["is_deleted"];

        // Determine effective state: are we currently deleted?
        // - local "delete" edit → deleted
        // - local "restore" edit → not deleted
        // - no local edit → use VT value
        const isDeletedLocal = edit?.editType === "delete";
        const isRestoredLocal = edit?.editType === "restore";
        const isCurrentlyDeleted =
          isDeletedLocal || (rowIsDeletedFromVT && !isRestoredLocal);

        const handleClick = () => {
          setDeliveryDateEdits((prev) => {
            const next = { ...prev };
            const prevEdit = next[editKey];

            if (isCurrentlyDeleted) {
              // Currently deleted → user wants to restore
              if (rowIsDeletedFromVT) {
                // VT says deleted → need explicit "restore" edit to override
                next[editKey] = {
                  editType: "restore",
                  editValue: 0,
                  displayValue: "restore",
                };
              } else {
                // VT says not deleted, user had local "delete" → just remove edit
                delete next[editKey];
              }
            } else {
              // Currently not deleted → user wants to delete
              if (prevEdit?.editType === "restore") {
                // User had local "restore" on a VT-deleted row → just remove edit
                delete next[editKey];
              } else {
                // Add "delete" edit (overwrites any prior date/number edit)
                next[editKey] = {
                  editType: "delete",
                  editValue: 1,
                  displayValue: "delete",
                };
              }
            }
            return next;
          });
        };

        return React.createElement(
          Tooltip,
          {
            title: isCurrentlyDeleted
              ? "Restore this row"
              : "Delete this row",
            arrow: true,
            placement: "right" as const,
            children: React.createElement(
              IconButton,
              {
                size: "small",
                onClick: handleClick,
                sx: { p: "4px" },
              },
              isCurrentlyDeleted
                ? React.createElement(RestoreIcon, {
                    fontSize: "small",
                    sx: { color: "text.secondary" },
                  })
                : React.createElement(DeleteOutlineIcon, {
                    fontSize: "small",
                    sx: { color: "text.secondary" },
                  })
            ),
          }
        );
      },
    });
  }

  // 1. planning_cycle (read-only, formatted as "Oct 2026")
  if (columnNames.includes("planning_cycle")) {
    allColumns.push({
      key: "planning_cycle",
      label: COLUMN_LABELS["planning_cycle"],
      align: "center" as const,
      sortable: true,
      onSort: (e: React.MouseEvent<HTMLTableCellElement>) =>
        onSort("planning_cycle", e),
      sortDirection: sortConfig.find((s) => s.key === "planning_cycle")
        ?.direction,
      sticky: true,
      renderCell: (row: any) => {
        return React.createElement(
          Box,
          { sx: { p: 1, height: "40px", display: "flex", alignItems: "center", justifyContent: "center" } },
          formatPlanningCycle(row["planning_cycle"])
        );
      },
    });
  }

  // 2. order_date (editable date picker)
  if (columnNames.includes("order_date")) {
    allColumns.push(
      buildEditableDateColumn({
        key: "order_date",
        label: COLUMN_LABELS["order_date"],
        editType: "order_date",
        minColumnName: "order_date",
        maxColumnName: "order_date",
        sortConfig,
        onSort,
        editMode: editMode ?? false,
        deliveryDateEdits,
        setDeliveryDateEdits,
        segments,
        sticky: true,
      })
    );
  }

  // 3. Segmentation columns (read-only): supplier, location, sku
  for (const seg of KNOWN_SEGMENT_COLUMNS) {
    if (columnNames.includes(seg)) {
      allColumns.push(buildReadOnlyColumn(seg, COLUMN_LABELS[seg] || prettifyColumnName(seg), sortConfig, onSort, seg === "supplier"));
    }
  }

  // 4. Enrichment columns (everything not in EXPLICIT_COLUMNS and not a known segment)
  for (const col of columnNames) {
    if (EXPLICIT_COLUMNS.has(col) || HIDDEN_COLUMNS.has(col) || KNOWN_SEGMENT_COLUMNS.includes(col)) continue;
    allColumns.push(buildReadOnlyColumn(col, COLUMN_LABELS[col] || prettifyColumnName(col), sortConfig, onSort, false));
  }

  // 5. default_lead_time (read-only, computed from min/max)
  if (columnNames.includes("default_lead_time_bucket_days_min")) {
    allColumns.push({
      key: "default_lead_time_bucket_days_min",
      label: COLUMN_LABELS["default_lead_time_bucket_days_min"],
      align: "center" as const,
      sortable: true,
      onSort: (e: React.MouseEvent<HTMLTableCellElement>) =>
        onSort("default_lead_time_bucket_days_min", e),
      sortDirection: sortConfig.find(
        (s) => s.key === "default_lead_time_bucket_days_min"
      )?.direction,
      renderCell: (row: any) => {
        return React.createElement(
          Box,
          { sx: { p: 1, height: "40px", display: "flex", alignItems: "center", justifyContent: "center" } },
          formatNumberRange(row["default_lead_time_bucket_days_min"], row["default_lead_time_bucket_days_max"])
        );
      },
    });
  }

  // 6. delivery_date (editable)
  if (columnNames.includes("delivery_date_min")) {
    allColumns.push(
      buildEditableDateColumn({
        key: "delivery_date",
        label: COLUMN_LABELS["delivery_date"],
        editType: "delivery_date",
        minColumnName: "delivery_date_min",
        maxColumnName: "delivery_date_max",
        sortConfig,
        onSort,
        editMode: editMode ?? false,
        deliveryDateEdits,
        setDeliveryDateEdits,
        segments,
        sticky: false,
      })
    );
  }

  // 7. calculated_lead_time (editable number input)
  if (columnNames.includes("calculated_lead_time_min")) {
    allColumns.push(
      buildEditableNumberColumn({
        key: "calculated_lead_time",
        label: COLUMN_LABELS["calculated_lead_time"],
        editType: "calculated_lead_time",
        minColumnName: "calculated_lead_time_min",
        maxColumnName: "calculated_lead_time_max",
        sortConfig,
        onSort,
        editMode: editMode ?? false,
        deliveryDateEdits,
        setDeliveryDateEdits,
        segments,
      })
    );
  }

  // 8. days_change (editable number input)
  if (columnNames.includes("days_change_vs_default_min")) {
    allColumns.push(
      buildEditableNumberColumn({
        key: "days_change_vs_default",
        label: COLUMN_LABELS["days_change_vs_default"],
        editType: "days_change",
        minColumnName: "days_change_vs_default_min",
        maxColumnName: "days_change_vs_default_max",
        sortConfig,
        onSort,
        editMode: editMode ?? false,
        deliveryDateEdits,
        setDeliveryDateEdits,
        segments,
        helpText: "Adjust to add or subtract days from the default lead time.",
      })
    );
  }

  // Wrap every non-action column's renderCell to overlay grey-out styling
  // on deleted rows, and disable interactivity
  const wrappedColumns: MUIFlatTableColumn[] = allColumns.map((col) => {
    if (col.key === "__delete_action") return col;
    const originalRenderCell = col.renderCell;
    const colAlign = col.align ?? "left";
    const justify =
      colAlign === "right"
        ? "flex-end"
        : colAlign === "center"
          ? "center"
          : "flex-start";
    return {
      ...col,
      renderCell: (row: any, rowIndex: number) => {
        const deleted = isRowDeleted(row, deliveryDateEdits, segments);
        const inner = originalRenderCell
          ? originalRenderCell(row, rowIndex)
          : String(row[col.key] ?? "");
        if (!deleted) return inner;
        return React.createElement(
          Box,
          {
            sx: {
              ...deletedCellSx,
              height: "100%",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: justify,
              pointerEvents: "none",
            },
          },
          inner
        );
      },
    };
  });

  return {
    columns: wrappedColumns,
    getRow,
    rowCount: visibleRows.length,
    arrowTable,
    columnTotals: {},
  };
}

// ─── Column Builders ───────────────────────────────────────────────

function buildReadOnlyColumn(
  key: string,
  label: string,
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>,
  onSort: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => void,
  sticky: boolean
): MUIFlatTableColumn {
  return {
    key,
    label,
    align: "left" as const,
    sortable: true,
    onSort: (e: React.MouseEvent<HTMLTableCellElement>) => onSort(key, e),
    sortDirection: sortConfig.find((s) => s.key === key)?.direction,
    sticky,
  };
}

interface EditableColumnArgs {
  key: string;
  label: string;
  editType: EditEntry["editType"];
  minColumnName: string;
  maxColumnName: string;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  onSort: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => void;
  editMode: boolean;
  deliveryDateEdits: DeliveryDateEdits;
  setDeliveryDateEdits: React.Dispatch<React.SetStateAction<DeliveryDateEdits>>;
  segments: string[];
  sticky?: boolean;
  helpText?: string;
}

function buildEditableDateColumn(args: EditableColumnArgs): MUIFlatTableColumn {
  const {
    key,
    label,
    editType,
    minColumnName,
    maxColumnName,
    sortConfig,
    onSort,
    editMode,
    deliveryDateEdits,
    setDeliveryDateEdits,
    segments,
  } = args;

  return {
    key,
    label,
    align: "center" as const,
    sortable: true,
    onSort: (e: React.MouseEvent<HTMLTableCellElement>) => onSort(key, e),
    sortDirection: sortConfig.find((s) => s.key === key)?.direction,
    sticky: args.sticky,
    width: 160,
    renderCell: (row: any) => {
      const editKey = buildEditKey(row, segments);
      const edit = deliveryDateEdits[editKey];
      const hasEdit = edit && edit.editType === editType;

      // Compute live display value from edits
      const liveValues = computeLiveValues(row, editKey, deliveryDateEdits);

      // Display: edited value > live computed value > min/max range from row
      let displayValue: string;
      if (hasEdit) {
        displayValue = edit.displayValue;
      } else if (editType === "order_date" && liveValues.orderDateDisplay != null) {
        displayValue = liveValues.orderDateDisplay;
      } else if (editType === "delivery_date" && liveValues.deliveryDateDisplay != null) {
        displayValue = liveValues.deliveryDateDisplay;
      } else {
        displayValue = formatDateRange(row[minColumnName], row[maxColumnName]);
      }

      const isEdited = hasEdit || (editType === "delivery_date" && liveValues.deliveryDateEdited) || (editType === "order_date" && liveValues.orderDateEdited);

      if (!editMode) {
        return React.createElement(
          Box,
          {
            sx: {
              p: 1,
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: isEdited ? "#fff3e0" : "transparent",
            },
          },
          displayValue
        );
      }

      // Date input — always editable. For ranges, show range label above picker.
      // For the order_date picker: if another edit on this row has orderDateMs
      // set (e.g. user edited order_date then days_change), show the edited
      // order_date — not the original.
      const editForRow = deliveryDateEdits[editKey];
      const currentISOValue = hasEdit
        ? formatDateISOFromMs(edit.editValue)
        : editType === "order_date" && editForRow?.orderDateMs != null
          ? formatDateISOFromMs(editForRow.orderDateMs)
          : editType === "order_date"
            ? formatDateISO(row["order_date"])
            : formatDateISOFromMs(liveValues.deliveryDateMsMin ?? toMs(row[minColumnName]));

      // For order_date: restrict picker to the planning cycle's month
      const dateMinMax =
        editType === "order_date"
          ? getMonthRange(String(row["planning_cycle"] ?? ""))
          : undefined;

      const dateInput = React.createElement(TextField, {
        type: "date",
        size: "small",
        variant: "outlined",
        value: currentISOValue,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          const newDateStr = e.target.value; // YYYY-MM-DD
          if (!newDateStr) return;
          const newDateMs = new Date(newDateStr + "T00:00:00Z").getTime();
          if (isNaN(newDateMs)) return;

          setDeliveryDateEdits((prev) => {
            const next = { ...prev };
            // Carry forward orderDateMs from prior edit, or set it if editing order_date
            const priorOrderDateMs = prev[editKey]?.orderDateMs;
            delete next[editKey];
            next[editKey] = {
              editType,
              editValue: newDateMs,
              displayValue: formatDateMMDDYY(new Date(newDateMs)),
              orderDateMs: editType === "order_date" ? newDateMs : priorOrderDateMs,
            };
            return next;
          });
        },
        sx: compactInputSx,
        inputProps: {
          ...compactInputProps,
          ...(dateMinMax && { min: dateMinMax.min, max: dateMinMax.max }),
        },
      });

      // For delivery_date with a range, show range label above the picker
      if (editType === "delivery_date") {
        const rangeLabel = displayValue;
        return React.createElement(
          Box,
          {
            sx: {
              position: "relative",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              width: "100%",
              height: "40px",
              backgroundColor: isEdited ? "#fff3e0" : "transparent",
            },
          },
          React.createElement(
            Box,
            {
              sx: {
                position: "absolute",
                top: "1px",
                fontSize: "0.6rem",
                color: "text.secondary",
                lineHeight: 1,
                whiteSpace: "nowrap",
                opacity: 0.8,
              },
            },
            rangeLabel
          ),
          dateInput
        );
      }

      return dateInput;
    },
  };
}

function buildEditableNumberColumn(
  args: EditableColumnArgs
): MUIFlatTableColumn {
  const {
    key,
    label,
    editType,
    minColumnName,
    maxColumnName,
    sortConfig,
    onSort,
    editMode,
    deliveryDateEdits,
    setDeliveryDateEdits,
    segments,
    helpText,
  } = args;

  return {
    key,
    label,
    align: "center" as const,
    sortable: true,
    onSort: (e: React.MouseEvent<HTMLTableCellElement>) => onSort(key, e),
    sortDirection: sortConfig.find((s) => s.key === key)?.direction,
    width: 140,
    renderHeader: helpText
      ? () =>
          React.createElement(
            Box,
            {
              sx: {
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                userSelect: "none",
              },
            },
            label,
            sortConfig.find((s) => s.key === key)?.direction === "asc"
              ? " ▲"
              : sortConfig.find((s) => s.key === key)?.direction === "desc"
                ? " ▼"
                : "",
            React.createElement(
              Tooltip,
              {
                title: helpText,
                arrow: true,
                placement: "top" as const,
                children: React.createElement(InfoOutlinedIcon, {
                  sx: {
                    fontSize: "14px",
                    color: "text.secondary",
                    cursor: "help",
                  },
                }),
              }
            )
          )
      : undefined,
    renderCell: (row: any) => {
      const editKey = buildEditKey(row, segments);
      const edit = deliveryDateEdits[editKey];
      const hasEdit = edit && edit.editType === editType;

      const liveValues = computeLiveValues(row, editKey, deliveryDateEdits);

      // Display: edited value > live computed value > min/max range from row
      let displayValue: string;
      if (hasEdit) {
        displayValue = edit.displayValue;
      } else if (editType === "calculated_lead_time" && liveValues.calculatedLeadTimeDisplay != null) {
        displayValue = liveValues.calculatedLeadTimeDisplay;
      } else if (editType === "days_change" && liveValues.daysChangeDisplay != null) {
        displayValue = liveValues.daysChangeDisplay;
      } else {
        displayValue = formatNumberRange(row[minColumnName], row[maxColumnName]);
      }

      const isEdited = hasEdit || liveValues.hasAnyEdit;

      if (!editMode) {
        return React.createElement(
          Box,
          {
            sx: {
              p: 1,
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: isEdited ? "#fff3e0" : "transparent",
            },
          },
          displayValue
        );
      }

      // Edit mode: show current display value, user can clear and type new
      const currentValue = hasEdit ? String(edit.editValue) : displayValue;

      return React.createElement(
        Box,
        {
          sx: {
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            width: "100%",
            height: "40px",
            backgroundColor: isEdited ? "#fff3e0" : "transparent",
          },
        },
        React.createElement(CompactTableTextField, {
          value: currentValue,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            if (val === "-") return;
            if (val === "") {
              setDeliveryDateEdits((prev) => {
                const next = { ...prev };
                const priorOrderDateMs = prev[editKey]?.orderDateMs;
                delete next[editKey];
                // If order_date was edited earlier, keep it as an order_date entry
                if (priorOrderDateMs != null) {
                  next[editKey] = {
                    editType: "order_date",
                    editValue: priorOrderDateMs,
                    displayValue: formatDateMMDDYY(new Date(priorOrderDateMs)),
                    orderDateMs: priorOrderDateMs,
                  };
                }
                return next;
              });
              return;
            }
            const numVal = parseInt(val, 10);
            if (isNaN(numVal)) return;

            setDeliveryDateEdits((prev) => {
              const next = { ...prev };
              // Carry forward orderDateMs from prior edit on this row
              const priorOrderDateMs = prev[editKey]?.orderDateMs;
              delete next[editKey];
              next[editKey] = {
                editType,
                editValue: numVal,
                displayValue: String(numVal),
                orderDateMs: priorOrderDateMs,
              };
              return next;
            });
          },
        })
      );
    },
  };
}

// ─── Live Cell Update Calculations ──────────────────────────────────

interface LiveValues {
  hasAnyEdit: boolean;
  orderDateEdited: boolean;
  deliveryDateEdited: boolean;
  orderDateDisplay: string | null;
  deliveryDateDisplay: string | null;
  deliveryDateMsMin: number | null;
  calculatedLeadTimeDisplay: string | null;
  daysChangeDisplay: string | null;
}

function computeLiveValues(
  row: any,
  editKey: string,
  edits: DeliveryDateEdits
): LiveValues {
  const edit = edits[editKey];
  if (!edit) {
    return {
      hasAnyEdit: false,
      orderDateEdited: false,
      deliveryDateEdited: false,
      orderDateDisplay: null,
      deliveryDateDisplay: null,
      deliveryDateMsMin: null,
      calculatedLeadTimeDisplay: null,
      daysChangeDisplay: null,
    };
  }

  const originalOrderDateMs = toMs(row["order_date"]);
  const defaultLeadTimeMin = toNumber(row["default_lead_time_bucket_days_min"]);
  const defaultLeadTimeMax = toNumber(row["default_lead_time_bucket_days_max"]);

  let orderDateMs = originalOrderDateMs;

  switch (edit.editType) {
    case "order_date": {
      orderDateMs = edit.editValue;
      // Preserve the current lead time (which may include prior edits like +30 days),
      // not the default lead time. This way editing order_date doesn't reset days_change.
      const currentDeliveryMin = toMs(row["delivery_date_min"]);
      const currentDeliveryMax = toMs(row["delivery_date_max"]);
      const currentLeadTimeMin = isNaN(currentDeliveryMin) ? defaultLeadTimeMin : Math.round((currentDeliveryMin - originalOrderDateMs) / MS_PER_DAY);
      const currentLeadTimeMax = isNaN(currentDeliveryMax) ? defaultLeadTimeMax : Math.round((currentDeliveryMax - originalOrderDateMs) / MS_PER_DAY);
      const deliveryMin = orderDateMs + currentLeadTimeMin * MS_PER_DAY;
      const deliveryMax = orderDateMs + currentLeadTimeMax * MS_PER_DAY;
      // Same-side subtraction: each SKU's days_change = its_lead_time - its_default
      const daysChangeMin = currentLeadTimeMin - defaultLeadTimeMin;
      const daysChangeMax = currentLeadTimeMax - defaultLeadTimeMax;
      return {
        hasAnyEdit: true,
        orderDateEdited: true,
        deliveryDateEdited: true,
        orderDateDisplay: formatDateMMDDYY(new Date(orderDateMs)),
        deliveryDateDisplay: formatDateRange(new Date(deliveryMin), new Date(deliveryMax)),
        deliveryDateMsMin: deliveryMin,
        calculatedLeadTimeDisplay: formatNumberRange(currentLeadTimeMin, currentLeadTimeMax),
        daysChangeDisplay: formatNumberRange(daysChangeMin, daysChangeMax),
      };
    }
    case "delivery_date": {
      const effectiveOrderDateMs = edit.orderDateMs ?? orderDateMs;
      const deliveryDateMs = edit.editValue;
      const calcLeadTime = Math.round((deliveryDateMs - effectiveOrderDateMs) / MS_PER_DAY);
      const daysChangeMin = calcLeadTime - defaultLeadTimeMax;
      const daysChangeMax = calcLeadTime - defaultLeadTimeMin;
      return {
        hasAnyEdit: true,
        orderDateEdited: edit.orderDateMs != null,
        deliveryDateEdited: true,
        orderDateDisplay: edit.orderDateMs != null
          ? formatDateMMDDYY(new Date(edit.orderDateMs))
          : null,
        deliveryDateDisplay: formatDateMMDDYY(new Date(deliveryDateMs)),
        deliveryDateMsMin: deliveryDateMs,
        calculatedLeadTimeDisplay: String(calcLeadTime),
        daysChangeDisplay: formatNumberRange(daysChangeMin, daysChangeMax),
      };
    }
    case "calculated_lead_time": {
      const effectiveOrderDateMs = edit.orderDateMs ?? orderDateMs;
      const calcLeadTime = edit.editValue;
      const deliveryDateMs = effectiveOrderDateMs + calcLeadTime * MS_PER_DAY;
      const daysChangeMin = calcLeadTime - defaultLeadTimeMax;
      const daysChangeMax = calcLeadTime - defaultLeadTimeMin;
      return {
        hasAnyEdit: true,
        orderDateEdited: edit.orderDateMs != null,
        deliveryDateEdited: true,
        orderDateDisplay: edit.orderDateMs != null
          ? formatDateMMDDYY(new Date(edit.orderDateMs))
          : null,
        deliveryDateDisplay: formatDateMMDDYY(new Date(deliveryDateMs)),
        deliveryDateMsMin: deliveryDateMs,
        calculatedLeadTimeDisplay: String(calcLeadTime),
        daysChangeDisplay: formatNumberRange(daysChangeMin, daysChangeMax),
      };
    }
    case "days_change": {
      // Use edited order_date if available (user edited both in this session)
      const effectiveOrderDateMs = edit.orderDateMs ?? orderDateMs;
      const daysChange = edit.editValue;
      const calcLeadTimeMin = defaultLeadTimeMin + daysChange;
      const calcLeadTimeMax = defaultLeadTimeMax + daysChange;
      const deliveryMin = effectiveOrderDateMs + calcLeadTimeMin * MS_PER_DAY;
      const deliveryMax = effectiveOrderDateMs + calcLeadTimeMax * MS_PER_DAY;
      return {
        hasAnyEdit: true,
        orderDateEdited: edit.orderDateMs != null,
        deliveryDateEdited: true,
        orderDateDisplay: edit.orderDateMs != null
          ? formatDateMMDDYY(new Date(edit.orderDateMs))
          : null,
        deliveryDateDisplay: formatDateRange(new Date(deliveryMin), new Date(deliveryMax)),
        deliveryDateMsMin: deliveryMin,
        calculatedLeadTimeDisplay: formatNumberRange(calcLeadTimeMin, calcLeadTimeMax),
        daysChangeDisplay: String(daysChange),
      };
    }
    case "delete":
    case "restore": {
      // Delete/restore are structural — they don't affect displayed values
      return {
        hasAnyEdit: false,
        orderDateEdited: false,
        deliveryDateEdited: false,
        orderDateDisplay: null,
        deliveryDateDisplay: null,
        deliveryDateMsMin: null,
        calculatedLeadTimeDisplay: null,
        daysChangeDisplay: null,
      };
    }
  }
}

// ─── Helpers ────────────────────────────────────────────────────────

/** Build edit key: "planning_cycle|seg1_value|seg2_value|..." */
/** Determine if a row is currently deleted (VT state + local edits). */
function isRowDeleted(
  row: any,
  edits: DeliveryDateEdits,
  segments: string[]
): boolean {
  const editKey = buildEditKey(row, segments);
  const edit = edits[editKey];
  const fromVT = !!row["is_deleted"];
  if (edit?.editType === "delete") return true;
  if (edit?.editType === "restore") return false;
  return fromVT;
}

/** Style overlay applied to cells of deleted rows.
 *  Targets inner MUI inputs so TextFields render greyed out and struck through. */
const deletedCellSx = {
  textDecoration: "line-through",
  color: "text.disabled",
  opacity: 0.6,
  backgroundColor: "#f5f5f5",
  // Grey out MUI input content + apply strikethrough inside TextFields
  "& input, & .MuiInputBase-input": {
    color: "rgba(0,0,0,0.38) !important",
    textDecoration: "line-through",
    WebkitTextFillColor: "rgba(0,0,0,0.38)",
    backgroundColor: "#f5f5f5",
  },
  // Native <input type="date"> renders its editable parts in webkit shadow DOM;
  // these selectors hit the visible date text and apply strikethrough there
  "& input[type='date']::-webkit-datetime-edit, & input[type='date']::-webkit-datetime-edit-fields-wrapper, & input[type='date']::-webkit-datetime-edit-text, & input[type='date']::-webkit-datetime-edit-month-field, & input[type='date']::-webkit-datetime-edit-day-field, & input[type='date']::-webkit-datetime-edit-year-field":
    {
      color: "rgba(0,0,0,0.38) !important",
      textDecoration: "line-through",
    },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0,0,0,0.12)",
  },
};

function buildEditKey(row: any, segments: string[]): string {
  const parts: string[] = [String(row["planning_cycle"] ?? "")];
  for (const seg of segments) {
    parts.push(String(row[seg] ?? ""));
  }
  return parts.join("|");
}

/**
 * Convert the VT's `finest_grain_state` list column value (an Arrow list
 * vector of structs, or null) into a plain array of {@link FinestGrainEntry}
 * objects that the save flow can iterate without touching Arrow internals.
 */
function toMs(value: any): number {
  if (value === null || value === undefined) return NaN;
  if (typeof value === "bigint") return Number(value / BigInt(1000));
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  const ms = new Date(value).getTime();
  return isNaN(ms) ? NaN : ms;
}

function toNumber(value: any): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === "bigint") return Number(value);
  if (typeof value === "number") return value;
  const n = Number(value);
  return isNaN(n) ? 0 : n;
}

/** Format "2026-10" as "Oct 2026" */
function formatPlanningCycle(value: any): string {
  if (!value) return "";
  const str = String(value);
  const match = str.match(/^(\d{4})-(\d{2})$/);
  if (!match) return str;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthIdx = parseInt(match[2], 10) - 1;
  if (monthIdx < 0 || monthIdx > 11) return str;
  return `${months[monthIdx]} ${match[1]}`;
}

/** Get min/max date strings (YYYY-MM-DD) for a planning cycle's date range.
 *  Monthly: "2026-06" → { min: "2026-06-01", max: "2026-06-30" }
 *  Weekly:  "2026-W15" → { min: Monday, max: Sunday of ISO week 15 } */
function getMonthRange(planningCycle: string): { min: string; max: string } | null {
  // Monthly: "2026-06"
  const monthMatch = planningCycle.match(/^(\d{4})-(\d{2})$/);
  if (monthMatch) {
    const year = parseInt(monthMatch[1], 10);
    const month = parseInt(monthMatch[2], 10);
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return {
      min: `${monthMatch[1]}-${monthMatch[2]}-01`,
      max: `${monthMatch[1]}-${monthMatch[2]}-${String(lastDay).padStart(2, "0")}`,
    };
  }
  // Weekly: "2026-W15"
  const weekMatch = planningCycle.match(/^(\d{4})-W(\d{1,2})$/);
  if (weekMatch) {
    const year = parseInt(weekMatch[1], 10);
    const week = parseInt(weekMatch[2], 10);
    // ISO week 1 contains Jan 4. Monday of week 1 = Jan 4 - dayOfWeek(Jan 4) + 1
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const monday = new Date(Date.UTC(year, 0, 4 - (jan4.getUTCDay() || 7) + 1 + (week - 1) * 7));
    const sunday = new Date(monday.getTime() + 6 * MS_PER_DAY);
    return {
      min: monday.toISOString().slice(0, 10),
      max: sunday.toISOString().slice(0, 10),
    };
  }
  return null;
}

/** Format a date range from min/max values */
function formatDateRange(min: any, max: any): string {
  const minStr = formatDateMMDDYY(min);
  const maxStr = formatDateMMDDYY(max);
  if (!minStr) return "";
  if (!maxStr || minStr === maxStr) return minStr;
  return `${minStr} - ${maxStr}`;
}

/** Format a number range from min/max values */
function formatNumberRange(min: any, max: any): string {
  const minNum = toNumber(min);
  const maxNum = toNumber(max);
  if (minNum === maxNum) return String(minNum);
  return `${minNum} - ${maxNum}`;
}

function formatDateMMDDYY(value: any): string {
  if (value === null || value === undefined) return "";
  const ms = value instanceof Date ? value.getTime() : toMs(value);
  if (isNaN(ms)) return typeof value === "string" ? value : "";
  const d = new Date(ms);
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const yy = String(d.getUTCFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
}

function formatDateISO(value: any): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const ms = toMs(value);
  if (isNaN(ms)) return String(value);
  return new Date(ms).toISOString().slice(0, 10);
}

function formatDateISOFromMs(ms: number): string {
  if (isNaN(ms)) return "";
  return new Date(ms).toISOString().slice(0, 10);
}

function prettifyColumnName(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const compactInputSx = {
  width: "100%",
  minWidth: 100,
  m: 0,
  p: 0,
  "& .MuiInputBase-root": {
    m: 0,
    p: 0,
    height: 28,
    minHeight: 24,
  },
  "& .MuiInputBase-input": {
    fontSize: "0.75rem",
    p: "2px 4px",
    height: 20,
    minHeight: 16,
  },
};

const compactInputProps = {
  style: {
    padding: 0,
    fontSize: "0.75rem",
    height: 20,
    minHeight: 16,
  },
};
